import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReelVideo } from './Reel';
import {
  AD_CATEGORIES,
  AdCategory,
  formatDuration,
  libraryAds,
  toReelCard,
} from '../data/ads';
import { track, EVENTS } from '../utils/analytics';
import '../styles/ads.css';

const CATEGORY_IDS = AD_CATEGORIES.map((c) => c.id);

const isCategory = (value: string): value is AdCategory =>
  (CATEGORY_IDS as readonly string[]).includes(value);

const categoryFromHash = (): AdCategory => {
  if (typeof window === 'undefined') return 'software';
  const hash = window.location.hash.replace('#', '');
  return isCategory(hash) ? hash : 'software';
};

/**
 * The /marketing library: one tab per category, a grid of lazily loaded
 * clips underneath. The active tab lives in the URL hash so a link can open
 * a category directly (/marketing#fight) and the back button works. Tabs
 * follow the WAI-ARIA tabs pattern: arrow keys move, focus follows selection.
 */
const AdLibrary: React.FC = () => {
  const [active, setActive] = useState<AdCategory>(categoryFromHash);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const onHash = () => setActive(categoryFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const select = useCallback((id: AdCategory, focus = false) => {
    setActive(id);
    if (typeof window !== 'undefined' && window.location.hash !== `#${id}`) {
      // replaceState keeps the page from jumping to an anchor; the hash is for sharing.
      window.history.replaceState(null, '', `#${id}`);
    }
    track(EVENTS.adLibraryTab, { category: id });
    if (focus) tabRefs.current[CATEGORY_IDS.indexOf(id)]?.focus();
  }, []);

  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    const last = CATEGORY_IDS.length - 1;
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (e.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();
    select(CATEGORY_IDS[next], true);
  };

  const ads = useMemo(() => libraryAds(active), [active]);
  const meta = AD_CATEGORIES.find((c) => c.id === active) ?? AD_CATEGORIES[0];

  return (
    <div className="adlib">
      <div className="adlib__tabs" role="tablist" aria-label="Ad categories">
        {AD_CATEGORIES.map((c, i) => {
          const selected = c.id === active;
          return (
            <button
              key={c.id}
              ref={(el) => {
                tabRefs.current[i] = el;
              }}
              type="button"
              role="tab"
              id={`adlib-tab-${c.id}`}
              aria-selected={selected}
              aria-controls={`adlib-panel-${c.id}`}
              tabIndex={selected ? 0 : -1}
              className={`adlib__tab${selected ? ' is-active' : ''}`}
              onClick={() => select(c.id)}
              onKeyDown={(e) => onKeyDown(e, i)}
            >
              {c.label}
              <span className="adlib__count" aria-hidden="true">
                {libraryAds(c.id).length}
              </span>
            </button>
          );
        })}
      </div>

      <div
        className="adlib__panel"
        role="tabpanel"
        id={`adlib-panel-${active}`}
        aria-labelledby={`adlib-tab-${active}`}
      >
        <p className="adlib__blurb">{meta.blurb}</p>
        <ul className="adlib__grid">
          {ads.map((ad) => (
            <li key={ad.id} className={`adlib__tile adlib__tile--${ad.layout}`}>
              <figure className="adlib__figure">
                <div className="adlib__media">
                  <ReelVideo card={toReelCard(ad)} />
                </div>
                <figcaption className="adlib__cap">
                  <span className="adlib__title">{ad.title}</span>
                  <span className="adlib__meta">
                    <span>{ad.client}</span>
                    <span aria-hidden="true">·</span>
                    <span>{formatDuration(ad.durationSec)}</span>
                  </span>
                  {ad.madeWith && <span className="adlib__made">{ad.madeWith}</span>}
                  {ad.instagram && (
                    <a className="adlib__ig" href={ad.instagram} target="_blank" rel="noopener noreferrer">
                      On Instagram
                      <span aria-hidden="true"> ↗</span>
                    </a>
                  )}
                </figcaption>
              </figure>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default React.memo(AdLibrary);
